<template>
  <div v-if="IsAdmin">
    <div class="q-pa-md">
      <div class="row items-center q-mb-md">
        <div class="text-h6">Transfer Ponds</div>
        <q-btn
          flat
          round
          icon="info"
          color="primary"
          size="sm"
          class="q-ml-sm"
          @click="showInfo = true"
        />
      </div>

      <q-dialog v-model="showInfo" position="top">
        <q-card style="width: 500px">
          <div class="row items-center q-py-sm q-px-md">
            <q-space />
            <div class="text-h5">Transfer Info</div>
            <q-space />
            <q-btn icon="clear" color="primary" v-close-popup />
          </div>
          <q-separator />
          <q-card-section>
            <div class="text-subtitle1 text-weight-bold q-mb-sm">How Transfer Works</div>
            <ul class="q-pl-md q-mb-md" style="line-height: 1.8">
              <li>Transfers a pond and all of its data (fish inventory, feeding history, harvest records, stockings, transfers, losses, and reconciliations) from one farm to another.</li>
              <li>The pond's FCR, TFA, and Purchaser will be cleared during transfer because these settings are specific to each farm.</li>
            </ul>

            <div class="text-subtitle1 text-weight-bold q-mb-sm">After Transfer</div>
            <ul class="q-pl-md q-mb-md" style="line-height: 1.8">
              <li>Go to the target farm's Settings > Ponds and edit the transferred pond to assign the correct FCR, TFA, and Purchaser. Fish data will not appear until FCR and TFA are set.</li>
              <li>If the target farm uses locations, the pond will be assigned to the first available location. You can change this in pond settings.</li>
            </ul>

            <div class="text-subtitle1 text-weight-bold q-mb-sm">Locations</div>
            <ul class="q-pl-md" style="line-height: 1.8">
              <li>If the target farm has locations, the transferred pond will be placed into a matching location (by name) or the first available location.</li>
              <li>If the target farm does not have locations, the pond's location will be cleared.</li>
            </ul>
          </q-card-section>
        </q-card>
      </q-dialog>

      <div class="row q-col-gutter-md q-mb-md">
        <div class="col-12 col-sm-6">
          <q-select
            v-model="sourceFarmId"
            :options="FarmOptions"
            label="Source Farm"
            emit-value
            map-options
            outlined
            dense
            clearable
          />
        </div>
        <div class="col-12 col-sm-6">
          <q-select
            v-model="targetFarmId"
            :options="TargetFarmOptions"
            label="Target Farm"
            emit-value
            map-options
            outlined
            dense
            clearable
            :disable="!sourceFarmId"
          />
        </div>
      </div>

      <q-separator />

      <div v-if="sourceFarmId && TransferPonds.length > 0" class="q-mt-md">
        <div class="row no-wrap items-center q-py-xs text-bold">
          <div class="col q-pl-sm" style="min-width: 120px">Name</div>
          <div class="col q-pl-sm" style="min-width: 120px">Location</div>
          <div class="col-auto q-pl-sm" style="width: 80px">Status</div>
          <div class="col-auto" style="width: 100px"></div>
        </div>

        <q-separator color="black" />

        <div v-for="pond in TransferPonds" :key="pond.id">
          <div class="row no-wrap items-center q-py-xs">
            <div class="col q-pl-sm" style="min-width: 120px">
              {{ decoder(pond.name) }}
            </div>
            <div class="col q-pl-sm" style="min-width: 120px">
              {{ pond.location_name ? decoder(pond.location_name) : '-' }}
            </div>
            <div class="col-auto q-pl-sm" style="width: 80px">
              <q-badge
                color="grey"
                :label="pond.archived ? 'Archived' : pond.active ? 'Active' : 'Inactive'"
              />
            </div>
            <div class="col-auto q-pa-xs" style="width: 100px">
              <q-btn
                unelevated
                label="Transfer"
                color="primary"
                size="sm"
                no-caps
                :disable="!targetFarmId"
                @click="doTransfer(pond)"
              />
            </div>
          </div>
          <q-separator />
        </div>
      </div>

      <div
        v-else-if="sourceFarmId && TransferPonds.length === 0"
        class="q-pa-md text-grey-6"
      >
        No ponds found on this farm.
      </div>

      <div v-else-if="!sourceFarmId" class="q-pa-md text-grey-6">
        Select a source farm to view its ponds.
      </div>
    </div>
  </div>
</template>

<script>
import { decoder } from '@/lib/helpers';
import store from '@/store';

export default {
  name: 'TransferPonds',
  data() {
    return {
      decoder,
      showInfo: false,
      sourceFarmId: null,
      targetFarmId: null,
      store
    };
  },
  computed: {
    IsAdmin() {
      return this.$store.state.user.feedx_level === 'super-admin';
    },
    FarmOptions() {
      return this.$store.state.adminFarms.map((f) => ({
        label: decoder(f.farm_name),
        value: f.id
      }));
    },
    TargetFarmOptions() {
      return this.FarmOptions.filter((f) => f.value !== this.sourceFarmId);
    },
    TransferPonds() {
      return (this.$store.state.transferPonds || [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    }
  },
  watch: {
    sourceFarmId(newVal) {
      if (newVal) {
        store.dispatch('loadTransferPonds', { farmId: newVal });
      } else {
        store.commit({ type: 'SET_TRANSFER_PONDS', item: [] });
      }
      if (this.targetFarmId === newVal) {
        this.targetFarmId = null;
      }
    }
  },
  methods: {
    doTransfer(pond) {
      const sourceFarm = this.$store.state.adminFarms.find(
        (f) => f.id === this.sourceFarmId
      );
      const targetFarm = this.$store.state.adminFarms.find(
        (f) => f.id === this.targetFarmId
      );

      this.$q
        .dialog({
          title: 'Transfer Pond',
          message: `Transfer "${decoder(pond.name)}" from ${decoder(
            sourceFarm.farm_name
          )} to ${decoder(
            targetFarm.farm_name
          )}? FCR, TFA, and Purchaser will be cleared on the transferred pond.`,
          ok: { unelevated: true, color: 'primary', label: 'Transfer', noCaps: true },
          cancel: { unelevated: true, color: 'grey-2', textColor: 'black', label: 'Cancel', noCaps: true },
          focus: 'none',
          persistent: true
        })
        .onOk(() => {
          store
            .dispatch('transferPond', {
              pondId: pond.id,
              sourceFarmId: this.sourceFarmId,
              targetFarmId: this.targetFarmId
            })
            .then((results) => {
              this.$finishResult.handleResultsAsync(results);
            });
        });
    }
  }
};
</script>
